import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import {
  allArticles,
  starScandalArticles,
  influencerArticles,
  dailyCompetitionArticles,
  exclusiveArticles,
  categories,
} from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {
  'star-scandal': '明星塌房追踪',
  'influencer-drama': '网红反差视频',
  'daily-competition': '每日大赛实时',
  'exclusive': '独家爆料',
  'all': '全部内容',
};

const categoryDescMap: Record<string, string> = {
  'star-scandal': '最新明星塌房事件第一手报道，还原事件真相',
  'influencer-drama': '探秘网红真实面目，揭露MCN行业内幕',
  'daily-competition': '每日大赛最新赛况，全程追踪精彩对决',
  'exclusive': '吃瓜网独家深度报道，其他平台看不到的猛料',
  'all': '浏览吃瓜网全部内容，不错过任何一个瓜',
};

const categoryImageMap: Record<string, string> = {
  'star-scandal': 'https://readdy.ai/api/search-image?query=Breaking%20news%20concept%20with%20shattered%20glass%20effect%20and%20dramatic%20red%20lighting%2C%20celebrity%20scandal%20theme%2C%20dark%20moody%20background%20with%20warm%20amber%20accents%2C%20editorial%20magazine%20cover%20style%2C%20cinematic%20atmosphere&width=1800&height=800&seq=cat-star-g6h3&orientation=landscape',
  'influencer-drama': 'https://readdy.ai/api/search-image?query=Social%20media%20screens%20showing%20contrasting%20personalities%2C%20split%20lighting%20effect%2C%20warm%20coral%20and%20deep%20brown%20tones%2C%20editorial%20photography%20style%2C%20clean%20minimal%20composition%2C%20dramatic%20reveal&width=1800&height=800&seq=cat-inf-j4k7&orientation=landscape',
  'daily-competition': 'https://readdy.ai/api/search-image?query=Grand%20stage%20with%20multiple%20spotlights%20and%20dramatic%20smoke%20effect%2C%20talent%20show%20atmosphere%2C%20warm%20golden%20and%20amber%20lighting%2C%20editorial%20performance%20photography%2C%20clean%20dark%20background&width=1800&height=800&seq=cat-comp-m2p9&orientation=landscape',
  'exclusive': 'https://readdy.ai/api/search-image?query=Top%20secret%20stamped%20document%20with%20magnifying%20glass%20on%20dark%20desk%2C%20dramatic%20shadow%20play%2C%20warm%20brown%20tones%2C%20investigative%20journalism%20style%2C%20editorial%20photography%2C%20mysterious%20atmosphere&width=1800&height=800&seq=cat-exc-r5s1&orientation=landscape',
  'all': 'https://readdy.ai/api/search-image?query=Entertainment%20news%20collage%20with%20multiple%20headlines%20and%20images%20floating%2C%20dramatic%20warm%20lighting%2C%20editorial%20magazine%20layout%20style%2C%20clean%20dark%20background%2C%20cinematic%20newsroom%20aesthetic&width=1800&height=800&seq=cat-all-t8v3&orientation=landscape',
};

function getArticles(slug: string) {
  switch (slug) {
    case 'star-scandal': return starScandalArticles;
    case 'influencer-drama': return influencerArticles;
    case 'daily-competition': return dailyCompetitionArticles;
    case 'exclusive': return exclusiveArticles;
    default: return allArticles;
  }
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const currentSlug = slug || 'all';
  const currentName = categoryNameMap[currentSlug] || '分类页面';
  const currentDesc = categoryDescMap[currentSlug] || '';
  const articles = getArticles(currentSlug);
  const heroImage = categoryImageMap[currentSlug] || categoryImageMap['all'];
  const pageUrl = `https://chiguawang.com/category/${currentSlug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('breadcrumbHome'),
        item: 'https://chiguawang.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: currentName,
        item: pageUrl,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="relative h-[280px] md:h-[380px] w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={currentName}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          </div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4 pt-16">
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50">
              {currentName}
            </h1>
            <p className="mt-4 text-sm md:text-base text-background-200/80 max-w-lg">
              {currentDesc}
            </p>
          </div>
        </section>

        <section className="bg-background-50 py-8 md:py-12">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('breadcrumbHome'), to: '/' },
                { label: currentName },
              ]}
            />

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 text-sm rounded-full transition-colors cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-600 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <ArticleCard
                  key={article.id}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  category={article.category}
                  categorySlug={article.categorySlug}
                  imageUrl={article.imageUrl}
                  date={article.date}
                  viewCount={article.viewCount}
                  commentCount={article.commentCount}
                  isHot={article.isHot}
                  isNew={article.isNew}
                />
              ))}
            </div>

            {articles.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-400">暂无相关内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}